/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    getMediaLocationsForProductAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric,
    MediaLocationsInput
} from '@msdyn365-commerce/core';
import { AsyncResult, MediaLocation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getMediaLocationsAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import {
    getSelectedVariant,
    SelectedVariantInput,
    generateImageUrl,
    getSelectedProductIdFromActionInput,
    QueryResultSettingsProxy
} from '@msdyn365-commerce-modules/retail-actions';
import * as Msdyn365 from '@msdyn365-commerce/core';

/**
 * Cleans up media location URLs by removing variant information separated by ^ characters.
 * Converts URLs like: /Products/877098 ^ 000000191 ^  ^  ^_000_001.png
 * To clean format: /Products/877098_000_001.png
 * Also handles URL-encoded versions (e.g., %5E for ^, %20 for space)
 * @param url - The URL to clean up
 * @returns The cleaned URL
 */
function cleanupMediaUrl(url: string): string {
    if (!url) {
        return url;
    }

    // Decode URL-encoded characters first (e.g., %5E -> ^, %20 -> space, %2F -> /)
    let decodedUrl: string;
    try {
        decodedUrl = decodeURIComponent(url);
    } catch {
        // If decoding fails, use original URL
        decodedUrl = url;
    }

    // Match pattern: productId ^ variant ^ ... ^_suffix.extension
    // Extract productId and _suffix.extension, removing the ^ separated parts
    const filenameMatch = decodedUrl.match(/fileName=\/Products\/(\d+)([^.]*)((_\d+_\d+)(\.\w+))$/i);
    if (filenameMatch) {
        const [fullMatch, productId, middlePart, suffixWithExt] = filenameMatch;
        // Check if middlePart contains ^ characters (indicates variant data to remove)
        if (middlePart && middlePart.includes('^')) {
            // Reconstruct URL without the ^ separated variant parts
            const cleanedUrl = decodedUrl.replace(fullMatch, `fileName=/Products/${productId}${suffixWithExt}`);
            return cleanedUrl;
        }
    }

    return decodedUrl;
}

/**
 * Validates if a media location's image exists by making an HTTP HEAD request.
 * @param mediaLocation - The media location to validate
 * @returns A promise that resolves to [mediaLocation, isValid] tuple
 */
async function validateMediaLocationAsync(mediaLocation: MediaLocation): Promise<[MediaLocation, boolean]> {
    const imageUrl = mediaLocation.Uri || '';

    if (imageUrl === '') {
        return [mediaLocation, false];
    }

    return new Promise<[MediaLocation, boolean]>(async resolve => {
        try {
            const response = await Msdyn365.sendRequest(imageUrl, 'head');
            const responseStatus = response.status;
            if (responseStatus === 200) {
                resolve([mediaLocation, true]);
            } else {
                resolve([mediaLocation, false]);
            }
        } catch {
            resolve([mediaLocation, false]);
        }
    });
}

/**
 * Validates an array of media locations and returns only those with existing images.
 * @param mediaLocations - Array of media locations to validate
 * @param ctx - Action context for logging
 * @returns A promise that resolves to an array of valid media locations
 */
async function validateMediaLocations(mediaLocations: MediaLocation[], ctx: IActionContext): Promise<MediaLocation[]> {
    try {
        const validationResults = await Promise.all(mediaLocations.map(mediaLocation => validateMediaLocationAsync(mediaLocation)));
        return validationResults.filter(([_, isValid]) => isValid).map(([mediaLocation]) => mediaLocation);
    } catch (error) {
        console.error('Error validating media locations:', error);
        ctx.telemetry.debug('Unable to validate media locations, returning all');
        return mediaLocations;
    }
}

/**
 * The action input class for the getMediaLocationsForSelectedVariant data action.
 */
export class MediaLocationsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    public selectedProduct: SimpleProduct | undefined;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
        this.selectedProduct = selectedProduct;
    }

    public getCacheKey = () => 'MediaLocationsForSelectedVariant';

    public getCacheObjectType = () => 'MediaLocations';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the getMediaLocationsForSelectedVariant data action.
 * @param inputData The input data passed to the createInput method.
 */
export const createMediaLocationsForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): MediaLocationsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    const catalogId = getCatalogId(inputData.requestContext);

    if (productId) {
        return new MediaLocationsForSelectedVariantInput(+productId, +inputData.requestContext.apiSettings.channelId, undefined, catalogId);
    }
    throw new Error('Unable to create MediaLocationsForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The action method for the getMediaLocationsForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getMediaLocationsForSelectedVariantAction(
    input: MediaLocationsForSelectedVariantInput,
    ctx: IActionContext
): Promise<MediaLocation[]> {
    return AsyncResult.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );
                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then(async productResult => {
            const activeProduct = productResult;

            if (activeProduct) {
                let mediaLocations: MediaLocation[] = [];

                if (ctx.requestContext.app?.config?.OmniChannelMedia) {
                    const mediaLocationsInput = new MediaLocationsInput(
                        ctx.requestContext,
                        activeProduct.RecordId,
                        input.channelId,
                        input.catalogId
                    );
                    mediaLocations = await getMediaLocationsForProductAction(mediaLocationsInput, ctx);
                } else {
                    const response = await getMediaLocationsAsync(
                        { callerContext: ctx, queryResultSettings: QueryResultSettingsProxy.getPagingFromInputDataOrDefaultValue(ctx) },
                        activeProduct.RecordId,
                        input.channelId,
                        input.catalogId
                    );
                    mediaLocations = response.map(
                        (mediaLocation: MediaLocation): MediaLocation => {
                            mediaLocation.Uri = generateImageUrl(mediaLocation.Uri, ctx.requestContext.apiSettings);
                            mediaLocation.AltText = mediaLocation.AltText ? mediaLocation.AltText : activeProduct.Name;
                            return mediaLocation;
                        }
                    );
                }

                // Apply URL cleanup to remove variant information from URLs
                const cleanedMediaLocations = mediaLocations.map(mediaLocation => ({
                    ...mediaLocation,
                    Uri: cleanupMediaUrl(mediaLocation.Uri ?? '')
                }));

                // Validate images and filter out non-existent ones
                const validatedMediaLocations = await validateMediaLocations(cleanedMediaLocations, ctx);

                return validatedMediaLocations;
            }

            return [];
        })
        .catch(error => {
            ctx.trace(error);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug('[getMediaLocationsForSelectedVariantAction]Error executing action');
            throw new Error('[getMediaLocationsForSelectedVariantAction]Error executing action');
        });
}

/**
 * The getMediaLocationsForSelectedVariant data action
 * Gets the currently selected variant for the page via the getSelectedVariant data action,
 * and then gets the media location information for the variant via the MediaLocations RetailServer API.
 */
export const getMediaLocationsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-media-locations-for-selected-variant',
    action: <IAction<MediaLocation[] | null>>getMediaLocationsForSelectedVariantAction,
    input: createMediaLocationsForSelectedVariantInput
});

export default getMediaLocationsForSelectedVariantActionDataAction;
